import { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useNavigate, Link } from '../components/nav'
import {
  ListChecks, RefreshCw, XCircle, Users, CheckCircle2, Clock, ExternalLink,
  ShieldAlert, ShieldCheck, Compass, Route, Plus, Pencil, CheckCircle, ChevronDown,
  ChevronRight, ArrowLeft, GitBranch,
} from 'lucide-react'
import Spinner from '../components/Spinner'
import { useIsAdmin } from '../hooks/useAdminMode'
import { useSignInOnUnauthorized } from '../hooks/useSignInOnUnauthorized'
import type { CreatedJourney, TestRun } from '../data/projectTypes'
import {
  useCreateExplorationMutation,
  useCreateTestRunMutation,
  useGetProjectQuery,
} from '../services/projectDataApi'
import { isParallelEligibleKind } from '../../netlify/functions/lib/task-kinds'
import { MetaItem, type BackTarget } from '../components/TaskDetailLayout'
import TaskShell from '../components/TaskShell'
import ReplayLink from '../components/ReplayLink'
import RunSummary from '../components/RunSummary'
import BugsFoundList, { type BugItem } from '../components/BugsFoundList'
import TaskEventReplay from '../components/TaskEventReplay'
import RecordingDataLinks from '../components/RecordingDataLinks'
import DuplicatesDetected from '../components/DuplicatesDetected'
import { CreatedJourneysList } from '../components/CreatedJourneysList'
import JourneySteps from '../components/JourneySteps'
import { journeyStopReason } from '../lib/journeyToolFailure'
import { apiFetch } from '../lib/apiFetch'
import { taskDetailUrl } from '../lib/taskDetailPath'
import { pickTargetUrl } from '../lib/targetUrl'
import './TestRunDetail.css'
import JourneyAuthoringData from '../components/JourneyAuthoringData'
import './ExplorationDetail.css'

// ─────────────────────────────────────────────────────────────────────────────
// THE UNIFIED TASK-DETAIL RENDERER.
//
// One component behind /projects/:projectId/tasks/:taskId for tasks, test runs, and explorations.
// It fetches a single envelope from GET /api/task-detail/:id and renders every section it has data for.
// ─────────────────────────────────────────────────────────────────────────────

function formatDurationDates(start: Date, end: Date): string {
  const s = Math.floor((end.getTime() - start.getTime()) / 1000)
  if (s < 60) return `${s}s`
  const m = Math.floor(s / 60)
  if (m < 60) return `${m}m ${s % 60}s`
  const h = Math.floor(m / 60)
  return `${h}h ${m % 60}m`
}
function formatDurationStr(start: string | null, end: string | null): string {
  if (!start) return '—'
  return formatDurationDates(new Date(start), end ? new Date(end) : new Date())
}

function followupStatusIcon(status: string) {
  if (status === 'completed') return <CheckCircle2 size={14} className="test-run-detail__followup-icon--completed" />
  if (status === 'failed') return <XCircle size={14} className="test-run-detail__followup-icon--failed" />
  if (status === 'in-progress') return <Spinner size={14} className="test-run-detail__followup-icon--in-progress" />
  return <Clock size={14} className="test-run-detail__followup-icon--pending" />
}
function followupStatusLabel(status: string): string {
  const labels: Record<string, string> = { pending: 'Pending', queued: 'Queued', 'in-progress': 'In progress', completed: 'Completed', failed: 'Failed' }
  return labels[status] ?? status.replace(/-/g, ' ')
}

// Container tasks (feedback/guidance/etc.) run with status 'running'; the other entity kinds use
// 'in-progress'. Include both so every not-yet-terminal task page keeps polling.
const ACTIVE_STATUSES = new Set(['in-progress', 'running', 'pending', 'queued'])

// A followup task's goal is "Recording followup: <label>"; show just the label on the run's link.
const RECORDING_FOLLOWUP_GOAL_PREFIX = 'Recording followup:'
function followupLabel(goal: string | null): string {
  if (!goal) return 'Followup'
  return goal.startsWith(RECORDING_FOLLOWUP_GOAL_PREFIX) ? goal.slice(RECORDING_FOLLOWUP_GOAL_PREFIX.length).trim() || 'Followup' : goal
}

// ── Envelope shapes (only the fields this renderer uses; the entities are opaque otherwise). ──
interface EnvTask { id: string; project_id: string; project_name?: string | null; goal: string | null; status: string; kind?: string | null; container_name: string | null; created_at: string; started_at: string | null; completed_at: string | null; deployed_url?: string | null; target_url?: string | null; bugs?: BugItem[]; duplicates?: unknown[]; logs?: unknown[]; mcp_errors?: unknown[]; backend_errors?: unknown[]; llm_jobs?: unknown[]; actions?: unknown[]; recording_id?: string | null; driver_recording_id?: string | null; followup_script?: string | null; originating_task_id?: string | null; followups?: EnvFollowup[] }
interface EnvFollowup { id: string; goal: string | null; status: string; followup_script: string | null; followup_args?: unknown; driver_recording_id?: string | null; created_at: string; started_at: string | null; completed_at: string | null }
interface EnvRun { outcome?: TestRun['outcome']; setup_output?: unknown; id: string; outcome_status?: string | null; outcome_reason?: string | null; project_name?: string | null; journey_name?: string | null; journey_id?: string | null; journey_version_id?: string | null; journey_version_number?: number | null; journey_step_count?: number | null; journey_judge_status?: string | null; journey_target_url?: string | null; environment_id?: string | null; environment_target_url?: string | null; override_url?: string | null; target_url?: string | null; deployed_url?: string | null; goal?: string | null; status: string; screenshots?: unknown[]; logs?: unknown[]; mcp_errors?: unknown[]; retriggered_from?: unknown; retriggers_from?: unknown[]; journey_updates?: Array<{ id: string; name: string; description: string | null; created_at: string; steps: unknown; summary: string | null; step_count: number }>; replay_recording_id?: string | null; replay_recording_ids?: Record<string, string> | null; driver_recording_id?: string | null; journey_steps?: unknown; created_at: string; started_at: string | null; completed_at: string | null; duration_seconds?: number | null; bugs_found_count?: number; bugs?: BugItem[]; file_journey_bug_task_id?: string | null; followups?: EnvFollowup[]; llm_jobs?: unknown[]; actions?: unknown[] }
interface EnvExplJourney { journey_id: string; journey_name: string; journey_description: string | null; step_count: number; action: string; judge_status: string | null }
interface EnvExpl { id: string; project_name?: string | null; status: string; target_url?: string | null; started_at: string | null; completed_at: string | null; journeys_created?: number; journeys_updated?: number; prompt?: string; logs?: unknown[]; mcp_errors?: unknown[]; llm_jobs?: unknown[]; actions?: unknown[]; retriggered_from?: unknown; retriggers_from?: unknown[]; replay_recording_id?: string | null; driver_recording_id?: string | null; journeys?: EnvExplJourney[]; journey_authoring?: unknown }
interface Envelope { task: EnvTask | null; test_run: EnvRun | null; exploration: EnvExpl | null }

function FollowupsSection({ followups, projectId }: { followups: EnvFollowup[] | undefined; projectId: string }) {
  const navigate = useNavigate()
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set())
  if (!followups?.length) return null
  const toggle = (id: string) => setExpanded(prev => {
    const next = new Set(prev)
    if (next.has(id)) next.delete(id); else next.add(id)
    return next
  })
  return (
    <section className="test-run-detail__section" data-testid="task-followups">
      <h2 className="test-run-detail__followups-heading"><GitBranch size={14} aria-hidden="true" /> Followups</h2>
      <div className="test-run-detail__followups-list" data-testid="followup-task-links">
        {followups.map(f => (
          <div key={f.id} className="test-run-detail__followup">
            <button className="test-run-detail__followup-item" onClick={() => navigate(taskDetailUrl(projectId, f.id))} data-testid={`followup-task-${f.id}`} data-analytics-event="task-followup-open">
              <span className="test-run-detail__followup-icon">{followupStatusIcon(f.status)}</span>
              <span className="test-run-detail__followup-name">{followupLabel(f.goal)}</span>
              <span className={`test-run-detail__followup-status test-run-detail__followup-status--${f.status}`}>{followupStatusLabel(f.status)}</span>
              <ExternalLink size={12} className="test-run-detail__followup-arrow" />
            </button>
            {f.driver_recording_id && <ReplayLink replayRecordingId={f.driver_recording_id} />}
            {f.followup_args != null && (
              <div className="test-run-detail__followup-args">
                <button type="button" className="test-run-detail__followup-args-toggle" onClick={() => toggle(f.id)} data-testid={`followup-args-toggle-${f.id}`} data-analytics-event="task-followup-args-toggle">
                  {expanded.has(f.id) ? <ChevronDown size={14} /> : <ChevronRight size={14} />} Pass 1 detector results
                </button>
                {expanded.has(f.id) && <pre className="test-run-detail__followup-args-json" data-testid={`followup-args-${f.id}`}>{JSON.stringify(f.followup_args, null, 2)}</pre>}
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  )
}

export default function TaskDetail() {
  const { projectId, taskId } = useParams<{ projectId: string; taskId: string }>()
  const navigate = useNavigate()
  const projectQuery = useGetProjectQuery(projectId ?? '', { skip: !projectId })
  // These pages are admin-only, so a signed-out admin following a task link would otherwise be told
  // the page is unavailable. A private project's 401 sends them to sign in and back here first.
  const redirectingToLogin = useSignInOnUnauthorized(projectQuery.error)
  const [createTestRun] = useCreateTestRunMutation()
  const [createExploration] = useCreateExplorationMutation()
  // Task pages are admin-only tooling. Gate on real admin status, NOT the preview toggle: the
  // admin-mode toggle only lives in the chat top bar and isn't reachable from a task page, so
  // gating here on the toggle would lock a real admin out with no way to turn it back on.
  const isAdmin = useIsAdmin()

  const [env, setEnv] = useState<Envelope | null>(null)
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)
  const [error, setError] = useState(false)

  const [retriggering, setRetriggering] = useState(false)

  const load = useCallback(async () => {
    // Task pages are admin-only; never call the (now admin-gated) task-detail endpoint for a
    // non-admin viewer — the page renders an access-denied notice instead (see below).
    if (!taskId || !isAdmin) return
    try {
      const res = await apiFetch(`/api/task-detail/${taskId}`)
      if (res.status === 404) { setNotFound(true); return }
      if (!res.ok) { setError(true); return }
      setEnv(await res.json() as Envelope)
      setError(false)
    } catch {
      setError(true)
    } finally {
      setLoading(false)
    }
  }, [taskId, isAdmin])

  useEffect(() => { load() }, [load])

  const task = env?.task ?? null
  const run = env?.test_run ?? null
  const expl = env?.exploration ?? null
  const primary: 'run' | 'exploration' | 'task' = run ? 'run' : expl ? 'exploration' : 'task'
  const entityLoaded = !!(task || run || expl)

  // Poll while anything is still active.
  useEffect(() => {
    const active = ACTIVE_STATUSES.has(run?.status ?? '') || ACTIVE_STATUSES.has(expl?.status ?? '')
      || ACTIVE_STATUSES.has(task?.status ?? '')
    if (!active) return
    const interval = setInterval(load, 10_000)
    return () => clearInterval(interval)
  }, [load, run?.status, expl?.status, task?.status])

  useEffect(() => {
    const name = run?.project_name || expl?.project_name || task?.project_name
    if (entityLoaded) document.title = `Replay QA: ${name || 'Task'}`
    return () => { document.title = 'Replay QA' }
  }, [entityLoaded, run, expl, task])

  // Task detail pages are admin-only. Non-admins (and admins previewing as a normal user via the
  // admin-mode toggle) never load the page — the backend endpoint enforces the same boundary. Show a
  // simple access notice with a way back to the project instead of the task chrome.
  if (!isAdmin) {
    // Render nothing for the moment it takes the sign-in redirect to leave the page.
    if (redirectingToLogin) return null
    return (
      <div className="task-detail" data-testid="task-detail-access-denied">
        <div className="task-detail__state">
          <p className="task-detail__state-message">This page is not available.</p>
          <Link to={`/projects/${projectId}/overview`} className="task-detail__state-back" data-analytics-event="task-access-denied-back">
            <ArrowLeft size={16} aria-hidden="true" /> Back to project
          </Link>
        </div>
      </div>
    )
  }

  async function retriggerRun() {
    if (!run?.journey_id || !projectId) return
    setRetriggering(true)
    try {
      const result = await createTestRun({
        project_id: projectId,
        journey_id: run.journey_id,
        journey_version_id: run.journey_version_id ?? undefined,
        goal: run.goal ?? undefined,
        environment_id: run.environment_id ?? undefined,
        override_url: run.override_url ?? undefined,
        retrigger: true,
      }).unwrap()
      navigate(taskDetailUrl(projectId, result.id))
    } catch { /* slice handles */ } finally { setRetriggering(false) }
  }
  async function retriggerExploration() {
    if (!expl?.prompt || !projectId) return
    setRetriggering(true)
    try {
      const result = await createExploration({ project_id: projectId, prompt: expl.prompt, retrigger: true }).unwrap()
      navigate(taskDetailUrl(projectId, result.id))
    } catch { /* slice handles */ } finally { setRetriggering(false) }
  }
  // ── Chrome (header/meta/footer + trailing sections) selected by the PRIMARY entity. ──
  let kindLabel = 'task'
  let testIdPrefix = 'task-detail'
  let notFoundBackTo = `/projects/${projectId}/overview`
  let notFoundBackLabel = 'Back to project'
  type ShellProps = React.ComponentProps<typeof TaskShell>
  let layout: ShellProps['layout']
  let logs: ShellProps['logs']
  let mcpErrors: ShellProps['mcpErrors']
  let backendErrors: ShellProps['backendErrors']
  let llmJobs: ShellProps['llmJobs']
  // The task's provenance graph (task_actions), from whichever entity this page resolved to.
  let taskActions: ShellProps['actions']
  let screenshots: ShellProps['screenshots']
  let retrigger: ShellProps['retrigger']

  if (primary === 'run' && run) {
    kindLabel = 'test run'; testIdPrefix = 'run'; notFoundBackTo = `/projects/${projectId}/test-runs`; notFoundBackLabel = 'Back to Test Runs'
    // Back to the journey this run exercised when we know it, since that's the page the run was most
    // likely opened from; otherwise to the run list.
    const back: BackTarget = run.journey_name && run.journey_id
      ? { label: run.journey_name, to: `/projects/${projectId}/journeys/${run.journey_id}` }
      : { label: 'Test Runs', to: `/projects/${projectId}/test-runs` }
    layout = {
      className: 'test-run-detail', testId: 'test-run-detail-page', back, title: 'Test Run', status: run.status,
      targetUrl: pickTargetUrl(run.override_url, run.environment_target_url, run.deployed_url, run.journey_target_url, run.target_url),
      headerExtra: (
        <>
          {run.journey_version_number != null && <span className="test-run-detail__journey-version">v{run.journey_version_number}</span>}
          {run.journey_step_count === 0 && <span className="test-run-detail__unstepped-badge">Unstepped</span>}
          {run.journey_judge_status === 'rejected' && <span className="test-run-detail__judge-badge test-run-detail__judge-badge--rejected"><ShieldAlert size={12} /> Journey Rejected</span>}
          {run.journey_judge_status === 'approved' && <span className="test-run-detail__judge-badge test-run-detail__judge-badge--approved"><ShieldCheck size={12} /> Journey Approved</span>}
          {/* The run's structured outcome REASON, next to its status chip. The status is only a coarse
              projection of the outcome (a bug and an unfileable finding are both 'failed'-adjacent), so the
              reason is what actually distinguishes two runs that look identical in the list. */}
          {run.outcome_reason && (
            <span className="test-run-detail__outcome-reason" data-testid="test-run-outcome-reason">
              {run.outcome_reason.replace(/-/g, ' ')}
            </span>
          )}
        </>
      ),
      footer: run.journey_id && projectId ? (
        <button type="button" className="test-run-detail__retrigger-btn" disabled={retriggering} data-testid="retrigger-run" data-analytics-event="test-run-retrigger" onClick={retriggerRun}>
          <RefreshCw size={14} aria-hidden />{retriggering ? 'Starting...' : 'Retrigger Run'}
        </button>
      ) : undefined,
    }
logs = run.logs as ShellProps['logs']; mcpErrors = run.mcp_errors as ShellProps['mcpErrors']; llmJobs = run.llm_jobs as ShellProps['llmJobs']; taskActions = run.actions as ShellProps['actions']; screenshots = run.screenshots as ShellProps['screenshots']
    retrigger = { entityType: 'test_run', retriggeredFrom: run.retriggered_from ?? null, retriggersFrom: run.retriggers_from ?? [], currentProjectId: projectId! } as ShellProps['retrigger']
  } else if (primary === 'exploration' && expl) {
    kindLabel = 'exploration'; testIdPrefix = 'exploration'; notFoundBackTo = `/projects/${projectId}/explorations`; notFoundBackLabel = 'Back to Explorations'
    const back: BackTarget = { label: 'Explorations', to: `/projects/${projectId}/explorations` }
    layout = {
      className: 'exploration-detail', testId: 'exploration-detail', back, icon: <Compass size={20} />, title: 'Exploration', status: expl.status,
      targetUrl: pickTargetUrl(expl.target_url),
      meta: (
        <>
          <MetaItem label="Started">{expl.started_at ? new Date(expl.started_at).toLocaleString() : '—'}</MetaItem>
          <MetaItem label="Duration">{formatDurationStr(expl.started_at, expl.completed_at)}</MetaItem>
          <MetaItem label="Journeys created">{expl.journeys_created}</MetaItem>
          <MetaItem label="Journeys updated">{expl.journeys_updated}</MetaItem>
        </>
      ),
      footer: projectId && expl.prompt ? (
        <button type="button" className="exploration-detail__retrigger-btn" disabled={retriggering} data-testid="retrigger-exploration" data-analytics-event="exploration-retry" onClick={retriggerExploration}>
          <RefreshCw size={14} aria-hidden />{retriggering ? 'Starting...' : 'Retrigger Exploration'}
        </button>
      ) : undefined,
    }
logs = expl.logs as ShellProps['logs']; mcpErrors = expl.mcp_errors as ShellProps['mcpErrors']; llmJobs = expl.llm_jobs as ShellProps['llmJobs']; taskActions = expl.actions as ShellProps['actions']
    retrigger = { entityType: 'exploration', retriggeredFrom: expl.retriggered_from ?? null, retriggersFrom: expl.retriggers_from ?? [], currentProjectId: projectId! } as ShellProps['retrigger']
  } else if (task) {
    const tk = task
    const back: BackTarget = { label: tk.project_name || 'Project', to: `/projects/${projectId}/overview` }
    layout = {
      testId: 'task-detail-page', back, icon: <ListChecks size={20} />, title: tk.goal || tk.id, status: tk.status,
      targetUrl: pickTargetUrl(tk.deployed_url, tk.target_url),
      meta: (
        <>
          <MetaItem label="Created">{new Date(tk.created_at).toLocaleString()}</MetaItem>
          {tk.started_at && <MetaItem label="Started">{new Date(tk.started_at).toLocaleString()}</MetaItem>}
          {tk.completed_at && <MetaItem label="Completed">{new Date(tk.completed_at).toLocaleString()}</MetaItem>}
          {tk.container_name && <MetaItem label="Container">{tk.container_name}</MetaItem>}
        </>
      ),
    }
    // A harness CHILD shares the parallel container, and its container logs live on the harness task's
    // page (the child's stdout IS the harness's — see container-events findRunningTask), so its page
    // shows only its own result + the driver recording. Keyed on the KIND's parallel eligibility, not on
    // "has a followup_script": a clone build carries one but is not harness-drained, and hiding the logs
    // of the container it owns outright left its page blank while it ran.
    logs = isParallelEligibleKind(tk.kind) ? [] : (tk.logs as ShellProps['logs'])
    mcpErrors = tk.mcp_errors as ShellProps['mcpErrors']; backendErrors = tk.backend_errors as ShellProps['backendErrors']; llmJobs = tk.llm_jobs as ShellProps['llmJobs']; taskActions = tk.actions as ShellProps['actions']
  }

  return (
    <TaskShell
      kindLabel={kindLabel}
      testIdPrefix={testIdPrefix}
      loading={loading && !entityLoaded && !notFound}
      error={error ? 'Failed to load task' : null}
      onRetry={load}
      entityLoaded={entityLoaded && !notFound}
      notFoundBackTo={notFoundBackTo}
      notFoundBackLabel={notFoundBackLabel}
      layout={layout}
      screenshots={screenshots}
      logs={logs}
      mcpErrors={mcpErrors}
      backendErrors={backendErrors}
      llmJobs={llmJobs}
      actions={taskActions}
      journeyChronology={run && task ? { taskId: task.id, active: ACTIVE_STATUSES.has(run.status) } : undefined}
      projectId={projectId}
      outcomeStatus={run?.outcome_status ?? null}
      retrigger={retrigger}
    >
      {/* ── Test run body ── */}
      {run && projectId && (
        <>
          {run.journey_updates && run.journey_updates.length > 0 && (
            <CreatedJourneysList
              heading="Journey Updated"
              journeys={run.journey_updates.map((u): CreatedJourney => ({ id: u.id, name: u.name, description: u.description, created_at: u.created_at, steps: u.steps, summary: u.summary, step_count: u.step_count, judge_status: null, judge_submissions: null }))}
              projectId={projectId}
              journeyLinkId={run.journey_id ?? undefined}
            />
          )}
          {task && <section className="test-run-detail__section" aria-labelledby="replay-preview-heading" data-testid="run-replay-preview">
            <h2 id="replay-preview-heading">Replay Preview</h2>
            <TaskEventReplay source={{ taskId: task.id }} active live={run.status === 'in-progress'} />
          </section>}
          {(run.replay_recording_id || (run.replay_recording_ids && Object.keys(run.replay_recording_ids).length > 0)) && (
            <section className="test-run-detail__section" aria-labelledby="replay-recording-heading" data-testid="run-recordings">
              <h2 id="replay-recording-heading">{run.replay_recording_ids && Object.keys(run.replay_recording_ids).length > 0 ? 'Replay Recordings' : 'Replay Recording'}</h2>
              {run.replay_recording_ids && Object.keys(run.replay_recording_ids).length > 0 ? (
                <div className="test-run-detail__recordings-list">
                  {Object.entries(run.replay_recording_ids).map(([ctx, recId]) => (
                    <div key={ctx} className="test-run-detail__recording-item" data-testid={`recording-${ctx}`}>
                      <span className="test-run-detail__recording-context"><Users size={12} /> {ctx}</span>
                      <ReplayLink replayRecordingId={recId} />
                    </div>
                  ))}
                </div>
              ) : <ReplayLink replayRecordingId={run.replay_recording_id!} />}
            </section>
          )}
          {run.driver_recording_id && (
            <section className="test-run-detail__section" data-testid="run-driver-recording">
              <h2>Driver Script Recording</h2>
              <ReplayLink replayRecordingId={run.driver_recording_id} />
            </section>
          )}
          {run.file_journey_bug_task_id && (
            <section className="test-run-detail__section" data-testid="run-file-journey-bug">
              <h2>Bug filing</h2>
              {/* A FAST MODE journey that saw a bug files it in a separate file-journey-bug task from
                  the recording; link to it so its progress + result are reachable from this run. */}
              <Link to={`/projects/${projectId}/tasks/${run.file_journey_bug_task_id}`} className="test-run-detail__file-journey-bug-link" data-testid="file-journey-bug-task-link">
                <ExternalLink size={14} aria-hidden="true" /> File journey bug task
              </Link>
            </section>
          )}
          <FollowupsSection followups={run.followups} projectId={projectId} />
          {(run.journey_step_count ?? 0) > 0 && (
            <section className="test-run-detail__section" data-testid="run-journey-actions">
              <JourneySteps projectId={projectId} run={run} steps={run.journey_steps} stepCount={run.journey_step_count ?? 0} lastRunStatus={run.status} lastRunError={journeyStopReason(run.outcome) ?? run.outcome_reason ?? null} />
            </section>
          )}
          {run.goal && !run.journey_name && (
            <section className="test-run-detail__goal" data-testid="run-goal">
              <h2 className="test-run-detail__goal-label">Goal</h2>
              <p className="test-run-detail__goal-text">{run.goal}</p>
            </section>
          )}
          <RunSummary queuedAt={run.created_at} startedAt={run.started_at ?? run.created_at} completedAt={run.completed_at} durationSeconds={run.duration_seconds ?? null} bugsFoundCount={run.bugs_found_count ?? 0} />
          <BugsFoundList bugs={(run.bugs ?? []) as BugItem[]} projectId={projectId} />
        </>
      )}

      {/* ── Exploration body ── */}
      {expl && projectId && (
        <>
          <div className="exploration-detail__prompt-section"><p className="exploration-detail__prompt">{expl.prompt}</p></div>
          <section className="exploration-detail__section" aria-labelledby="exploration-replay-preview-heading" data-testid="exploration-replay-preview">
            <h2 className="exploration-detail__section-title" id="exploration-replay-preview-heading">Replay Preview</h2>
            <TaskEventReplay source={{ explorationId: expl.id }} active live={expl.status !== 'completed' && expl.status !== 'failed'} />
          </section>
          {expl.replay_recording_id && (
            <section className="exploration-detail__section" data-testid="exploration-recording">
              <h2 className="exploration-detail__section-title">Browser Recording</h2>
              <ReplayLink replayRecordingId={expl.replay_recording_id} />
            </section>
          )}
          {expl.driver_recording_id && (
            <section className="exploration-detail__section" data-testid="exploration-driver-recording">
              <h2 className="exploration-detail__section-title">Driver Script Recording</h2>
              <ReplayLink replayRecordingId={expl.driver_recording_id} />
            </section>
          )}
          <section className="exploration-detail__section">
            <h2 className="exploration-detail__section-title"><Route size={15} /> Journeys ({(expl.journeys ?? []).length})</h2>
            {(expl.journeys ?? []).length === 0 ? <p className="exploration-detail__empty">No journeys found.</p> : (
              <div className="exploration-detail__table-wrap">
                <table className="exploration-detail__journeys-table">
                  <thead><tr><th>Action</th><th>Name</th><th>Description</th><th>Steps</th><th>Judge</th></tr></thead>
                  <tbody>
                    {(expl.journeys ?? []).map((j, idx) => (
                      <tr key={`${j.journey_id}-${idx}`} className="exploration-detail__journey-row" onClick={() => navigate(`/projects/${projectId}/journeys/${j.journey_id}`)} data-analytics-event="exploration-journey-row">
                        <td className="exploration-detail__journey-action-cell">
                          {j.action === 'created' ? <span className="exploration-detail__badge exploration-detail__badge--created"><Plus size={11} /> Created</span> : <span className="exploration-detail__badge exploration-detail__badge--updated"><Pencil size={11} /> Updated</span>}
                        </td>
                        <td className="exploration-detail__journey-name">{j.journey_name}</td>
                        <td className="exploration-detail__journey-desc">{j.journey_description || '—'}</td>
                        <td className="exploration-detail__journey-steps">{j.step_count} {j.step_count === 1 ? 'step' : 'steps'}</td>
                        <td className="exploration-detail__journey-judge">
                          {j.judge_status === 'approved' && <span className="exploration-detail__badge exploration-detail__badge--approved"><CheckCircle size={11} /> Approved</span>}
                          {j.judge_status === 'rejected' && <span className="exploration-detail__badge exploration-detail__badge--rejected"><XCircle size={11} /> Rejected</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
          {/* How those journeys came to be written. The wad is guidance-owned; the component reads it
              defensively and falls back to raw JSON for a shape it doesn't recognize. */}
          <JourneyAuthoringData data={expl.journey_authoring} />
        </>
      )}

      {/* ── Plain task body (goal-only container tasks with no entity of their own). ── */}
      {primary === 'task' && task && projectId && (
        <>
          <FollowupsSection followups={task.followups} projectId={projectId} />
          {task.followup_script && task.originating_task_id && (
            <section className="test-run-detail__section" data-testid="task-originating-link">
              <h2 className="test-run-detail__followups-heading"><GitBranch size={14} aria-hidden="true" /> Originating task</h2>
              {/* This is a followup task spawned by another run; link back to the task that triggered it. */}
              <Link to={`/projects/${projectId}/tasks/${task.originating_task_id}`} className="test-run-detail__file-journey-bug-link" data-testid="originating-task-link">
                <ArrowLeft size={14} aria-hidden="true" /> Back to originating task
              </Link>
            </section>
          )}
          {task.driver_recording_id && (
            <section className="test-run-detail__section" data-testid="task-driver-recording">
              <h2>Driver Script Recording</h2>
              <ReplayLink replayRecordingId={task.driver_recording_id} />
            </section>
          )}
          {task.recording_id && <RecordingDataLinks recordingId={task.recording_id} label="Recording data:" />}
          {(task.bugs?.length ?? 0) > 0 && <BugsFoundList bugs={(task.bugs ?? []) as BugItem[]} projectId={projectId} heading="Bugs Filed" />}
          {(task.duplicates?.length ?? 0) > 0 && <DuplicatesDetected duplicates={(task.duplicates ?? []) as never[]} projectId={projectId} />}
        </>
      )}
    </TaskShell>
  )
}
